import { createContext, useContext } from "react"
import type { FramePadding } from "./sheetPhysics"

/**
 * What the map renderers need to know about the mobile results sheet (#856).
 * Provided by MobileResultsLayout while the sheet layout is active; the
 * default (inactive) value is a frozen constant so an explorer without the
 * variant — and every existing test — sees no change in identity or
 * behavior.
 *
 * Deliberately NO per-snap state in here: the value is identity-stable for
 * the life of the layout. The renderers derive `properties` afresh on every
 * render (LeafletMap.tsx), so a context change per snap re-ran their marker
 * effect — rebuilding every marker and re-framing the map on each drag
 * settle (probed 8/26: 24 marker-pane mutations + a 138px pan per snap).
 * The current snap is read live through `getFramePadding` instead.
 */
export interface MobileResultsValue {
  /** True only while the sheet layout is mounted (client-side, under the breakpoint). */
  active: boolean
  /**
   * Padding (px, relative to the map element) that insets a programmatic
   * fit into the band the visitor can see — below the search CTA, above the
   * sheet at its current snap. Null when inactive, so the renderers keep
   * their default framing. Read LIVE at fit time; stable identity.
   */
  getFramePadding: (mapEl: HTMLElement | null) => FramePadding | null
}

export const INACTIVE_MOBILE_RESULTS: MobileResultsValue = Object.freeze({
  active: false,
  getFramePadding: () => null,
})

export const MobileResultsContext = createContext<MobileResultsValue>(INACTIVE_MOBILE_RESULTS)

export function useMobileResults(): MobileResultsValue {
  return useContext(MobileResultsContext)
}
